import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import { setTokenGetter } from '../api/client';

interface TokenPayload {
  sub: string;
  email?: string;
  name?: string;
  'cognito:groups'?: string[];
  exp: number;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: TokenPayload | null;
  accessToken: string | null;
  login: () => void;
  logout: () => void;
  handleCallback: (code: string) => Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  accessToken: null,
  login: () => {},
  logout: () => {},
  handleCallback: async () => {},
});

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI;

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateCodeVerifier(): Promise<string> {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
}

function decodeToken(token: string): TokenPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload as TokenPayload;
  } catch {
    return null;
  }
}

function isTokenExpired(payload: TokenPayload): boolean {
  return Date.now() >= payload.exp * 1000;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<TokenPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTokenRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<number | null>(null);

  const clearAuth = useCallback(() => {
    setTokenGetter(() => null);
    setAccessToken(null);
    setUser(null);
    refreshTokenRef.current = null;
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
  }, []);

  const scheduleRefresh = useCallback((expiresIn: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    // Refresh 60 seconds before expiry
    const refreshMs = Math.max((expiresIn - 60) * 1000, 10000);
    refreshTimerRef.current = window.setTimeout(async () => {
      if (!refreshTokenRef.current) return;
      try {
        const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: CLIENT_ID,
            refresh_token: refreshTokenRef.current,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          const nextAccessToken = data.access_token as string;
          // Update the API client synchronously so child effects cannot observe
          // the refreshed React state before the request token is available.
          setTokenGetter(() => nextAccessToken);
          setAccessToken(nextAccessToken);
          const decoded = decodeToken(nextAccessToken);
          if (decoded) setUser(decoded);
          if (data.refresh_token) {
            refreshTokenRef.current = data.refresh_token;
          }
          scheduleRefresh(data.expires_in || 3600);
        } else {
          clearAuth();
        }
      } catch {
        clearAuth();
      }
    }, refreshMs);
  }, [clearAuth]);

  const login = useCallback(async () => {
    const verifier = await generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    // Store verifier in sessionStorage (short-lived, needed for callback)
    sessionStorage.setItem('pkce_code_verifier', verifier);
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: 'openid email profile',
      code_challenge_method: 'S256',
      code_challenge: challenge,
    });
    window.location.href = `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
  }, []);

  const handleCallback = useCallback(async (code: string) => {
    const verifier = sessionStorage.getItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_code_verifier');
    if (!verifier) {
      throw new Error('Missing PKCE code verifier');
    }

    const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        code,
        code_verifier: verifier,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Token exchange failed: ${err}`);
    }

    const data = await response.json();
    const nextAccessToken = data.access_token as string;
    // Bind the token before the callback navigates to a protected route.
    // Dashboard effects can run before parent effects, so delayed registration
    // in Layout caused a successful login to redirect straight back to /login.
    setTokenGetter(() => nextAccessToken);
    setAccessToken(nextAccessToken);
    const decoded = decodeToken(nextAccessToken);
    if (decoded) setUser(decoded);
    if (data.refresh_token) {
      refreshTokenRef.current = data.refresh_token;
    }
    scheduleRefresh(data.expires_in || 3600);
    setIsLoading(false);
  }, [scheduleRefresh]);

  const logout = useCallback(() => {
    clearAuth();
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      logout_uri: window.location.origin + '/login',
    });
    window.location.href = `${COGNITO_DOMAIN}/logout?${params.toString()}`;
  }, [clearAuth]);

  useEffect(() => {
    // On mount, if there's no token, mark as not loading
    // Tokens are in-memory only, so a refresh requires re-login
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!accessToken && !!user && !isTokenExpired(user),
        isLoading,
        user,
        accessToken,
        login,
        logout,
        handleCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
